import axios, { Cancel, CancelToken } from 'axios';
import { apiBase } from 'services/apiBaseUrl';
import { buildWebUrl } from 'utils/webOrigin';
import { dispatchImpersonationExpired, getStoreState } from 'utils/authStateRegistry';
import { getActiveAuthHeaders, isImpersonatingState } from 'utils/getActiveAuthHeaders';
import { isReactNativeWebView } from 'utils/webAuthn';
import { hasWebViewSession } from 'utils/webViewSession';
import to from 'util-to';

const instance = axios.create({
  baseURL: apiBase(),
  timeout: 180000,
});

const instanceS3 = axios.create({
  timeout: 20000,
});

const http = {
  handleError(err, url) {
    if (err) {
      if (err.response) {
        if (err.request.responseURL && err.request.responseURL.includes('/api/v1/token')) {
          throw err.response;
        }
        if (err.response.status === 401 && !url.startsWith('/v1/token')) {
          // A 401 for a bearer that is no longer the active one (an impersonation token
          // revoked while this request was in flight) says nothing about the current session.
          const requestAuth =
            err.config?.headers?.Authorization || err.config?.headers?.authorization;
          const activeAuth = getActiveAuthHeaders().authorization;
          if (requestAuth && activeAuth && requestAuth !== activeAuth) {
            throw err.response;
          }
          // An impersonation-token 401 must drop back to the superadmin session, not log out.
          if (isImpersonatingState(getStoreState())) {
            dispatchImpersonationExpired();
            throw err.response;
          }
          const requestHadAuth = Boolean(
            err.config?.headers?.Authorization || err.config?.headers?.authorization,
          );
          // Guest /me probes without a bearer must not bounce through /logout (login ↔ logout loop).
          // The RN OmmnioSign WebView does not own the app session (injected on 2.24+, cookie on
          // older store builds). A 401 must stay an in-page error, never /logout, which deletes
          // the same bearer the native app is using.
          if (requestHadAuth && !hasWebViewSession() && !isReactNativeWebView()) {
            window.location.replace(buildWebUrl('/logout'));
          }
          throw err.response;
        }
        throw err.response;
      } else if (err instanceof Cancel) {
        throw new Error('Canceled');
      } else {
        throw err;
      }
    }
  },
  // `skipAuthorization` suppresses only this client's Authorization header. Browsers may still
  // attach same-origin cookies; callers must not treat this flag as a cookie transport control.
  authorize(inToken, skipAuthorization = false) {
    const headers = skipAuthorization ? {} : getActiveAuthHeaders();
    if (inToken?.hash) {
      headers.authorization = `Bearer ${inToken.hash}`;
    }
    const { headers: defaults } = instance.defaults;
    if (!headers.authorization) {
      delete defaults.common.Authorization;
      delete defaults['x-company'];
      delete defaults.common['x-impersonate'];
      delete defaults['x-impersonate'];
      return;
    }
    defaults.common.Authorization = headers.authorization;
    if (headers['x-company']) {
      defaults['x-company'] = headers['x-company'];
    } else {
      delete defaults['x-company'];
    }
    delete defaults.common['x-impersonate'];
    delete defaults['x-impersonate'];
  },
  async request(method, url, params, config) {
    if (config && config.getCancelFunc) {
      config.cancelToken = new CancelToken(config.getCancelFunc);
    }
    this.authorize(config?.token, config?.skipAuthorization);
    // axios' get signature is get(url, config) — passing (url, null, config) silently drops the
    // config (per-request headers, timeout), leaving only the instance defaults on the wire.
    const [err, response] = await to(
      method === 'get'
        ? instance.get(url, config ?? undefined)
        : instance[method](url, params, config),
    );
    this.handleError(err, url);

    return response?.data ?? null;
  },
  async get(url, config) {
    return this.request('get', url, null, config);
  },
  async post(url, params, config) {
    return this.request('post', url, params, config);
  },
  async put(url, params, config) {
    return this.request('put', url, params, config);
  },
  async delete(url, params) {
    return this.request('delete', url, params);
  },
  async patch(url, params) {
    return this.request('patch', url, params);
  },
  async putS3(url, params, config) {
    if (config && config.getCancelFunc) {
      config.cancelToken = new CancelToken(config.getCancelFunc);
    }
    const [err, response] = await to(instanceS3.put(url, params, config));
    this.handleError(err);

    return response?.data ?? null;
  },

  async getS3(url, params, config) {
    const [err, response] = await to(instanceS3.get(url, params, config));
    this.handleError(err);
    if (response) {
      return {
        data: response.data,
        contentType: response.headers['content-type'],
        length: response.headers['content-length'],
      };
    }
    return null;
  },
};

export default http;
